/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  Tooltip,
} from '@mui/material';
import { 
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  Message as MessageIcon,
  SwapVert as SwapIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useDebate } from '../contexts/DebateContext';
import { useAuth } from '../contexts/AuthContext';
import { DebateView } from '../types';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import logger from '../utils/logger';
// Removed useAsyncTranslation import - now using proper t() translation keys

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

const DebateFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { generateViews, startNewDebate, loading: contextLoading } = useDebate();
  const { isAuthenticated, redirectAfterLogin } = useAuth();
  
  // All texts now use proper translation keys
  // (removed useAsyncTranslation calls - now using t() function directly)
  
  // Constants for character limits
  const TOPIC_MAX_LENGTH = 200;
  const DESCRIPTION_MAX_LENGTH = 1000;
  const VIEW_MAX_LENGTH = 1000;
  
  // State for form steps
  const [activeStep, setActiveStep] = useState(0);
  const steps = [t('enterTopic'), t('reviewGeneratedViews'), t('customize'), t('overview')];
  
  // Form state
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [generatedViews, setGeneratedViews] = useState<DebateView | null>(null);
  const [customizedViews, setCustomizedViews] = useState<DebateView | null>(null);
  const [teamName, setTeamName] = useState('The Musketeers');
  const [teamViewpoint, setTeamViewpoint] = useState('');
  const [opponentTeamName, setOpponentTeamName] = useState('The Guardians');
  const [customPrompt, setCustomPrompt] = useState('');
  const [customCounterPrompt, setCustomCounterPrompt] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [teamInviteLink, setTeamInviteLink] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Validation error state
  const [validationErrors, setValidationErrors] = useState({
    topic: '',
    teamName: '',
    opponentTeamName: '',
    general: ''
  });
  
  // Invitation state
  const [createdDebate, setCreatedDebate] = useState<any>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  
  // Add isPublic state
  const [isPublic, setIsPublic] = useState(false);
  
  // Team configuration state
  const [teamSize, setTeamSize] = useState<number>(1);
  const [opponentTeamSize, setOpponentTeamSize] = useState<number>(1);
  const [publicSpots, setPublicSpots] = useState<number>(0);
  const [opponentPublicSpots, setOpponentPublicSpots] = useState<number>(0);
  const [publicJoinable, setPublicJoinable] = useState(false);
  // Optional password for opponent invite link
  const [invitePasswordEnabled, setInvitePasswordEnabled] = useState(false);
  const [invitePassword, setInvitePassword] = useState('');
  
  // Load saved state from localStorage on mount
  useEffect(() => {
    const savedState = localStorage.getItem('debateFormState');
    if (savedState) {
      const state = JSON.parse(savedState);
      setTopic(state.topic || '');
      setDescription(state.description || '');
      setGeneratedViews(state.generatedViews || null);
      setCustomizedViews(state.customizedViews || null);
      setTeamName(state.teamName || 'The Musketeers');
      setTeamViewpoint(state.teamViewpoint || '');
      setOpponentTeamName(state.opponentTeamName || 'The Guardians');
      setTeamSize(state.teamSize || 1);
      setOpponentTeamSize(state.opponentTeamSize || 1);
      setPublicSpots(state.publicSpots || 0);
      setOpponentPublicSpots(state.opponentPublicSpots || 0);
      setPublicJoinable(state.publicJoinable || false);
      setInvitePasswordEnabled(state.invitePasswordEnabled || false);
      setInvitePassword(state.invitePassword || '');
      setActiveStep(state.activeStep || 0);
      setIsPublic(state.isPublic || false);
    }
  }, []);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      topic,
      description,
      generatedViews,
      customizedViews,
      teamName,
      teamViewpoint,
      opponentTeamName,
      customPrompt,
      customCounterPrompt,
      activeStep,
      isPublic,
      teamSize,
      opponentTeamSize,
      publicSpots,
      opponentPublicSpots,
      publicJoinable,
      invitePasswordEnabled,
      invitePassword
    };
    localStorage.setItem('debateFormState', JSON.stringify(state));
  }, [topic, description, generatedViews, customizedViews, teamName, teamViewpoint, opponentTeamName, customPrompt, customCounterPrompt, activeStep, isPublic, teamSize, opponentTeamSize, publicSpots, opponentPublicSpots, publicJoinable, invitePasswordEnabled, invitePassword]);

  // Clear saved state when component unmounts
  useEffect(() => {
    return () => {
      localStorage.removeItem('debateFormState');
    };
  }, []);
  
  // Handle topic submission and view generation
  const handleGenerateViews = async () => {
    if (!isAuthenticated) {
      redirectAfterLogin('/debate/new');
      return;
    }
    
    // Clear previous validation errors
    setValidationErrors(prev => ({ ...prev, topic: '', general: '' }));
    
    if (!topic.trim()) {
      setValidationErrors(prev => ({ ...prev, topic: t('pleaseEnterDebateTopic') }));
      return;
    }
    
    try {
      setLoading(true);
      const views = await generateViews(topic, description);
      setGeneratedViews(views);
      setCustomPrompt(views.prompt);
      setCustomCounterPrompt(views.counterPrompt);
      setActiveStep(1);
    } catch (err) {
      logger.error('Failed to generate views:', err);
      setError(t('failedToGenerateViews'));
      setValidationErrors(prev => ({ ...prev, general: t('failedToGenerateViews') }));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle view customization
  const handleCustomizeViews = () => {
    if (!isAuthenticated) {
      redirectAfterLogin('/debate/new');
      return;
    }
    if (generatedViews) {
      const customizedViews = {
        ...generatedViews,
        prompt: customPrompt,
        counterPrompt: customCounterPrompt,
      };
      setCustomizedViews(customizedViews);
      setActiveStep(2);
    }
  };

  // Handle team customization
  const handleCustomizeTeam = () => {
    if (!isAuthenticated) {
      redirectAfterLogin('/debate/new');
      return;
    }
    
    // Clear previous validation errors
    setValidationErrors(prev => ({ ...prev, teamName: '', opponentTeamName: '' }));
    
    let hasErrors = false;
    
    if (!teamName.trim()) {
      setValidationErrors(prev => ({ ...prev, teamName: t('pleaseEnterTeamName') }));
      hasErrors = true;
    }
    
    if (!opponentTeamName.trim()) {
      setValidationErrors(prev => ({ ...prev, opponentTeamName: t('pleaseEnterOpponentTeamName') }));
      hasErrors = true;
    }
    
    if (hasErrors) {
      return;
    }
    
    setActiveStep(3);
  };
  
  // Create the debate
  const handleCreateDebate = async () => {
    if (!isAuthenticated) {
      redirectAfterLogin('/debate/new');
      return;
    }
    
    // Clear previous validation errors
    setValidationErrors(prev => ({ ...prev, general: '' }));
    
    if (!topic || !generatedViews) {
      setValidationErrors(prev => ({ ...prev, general: t('missingRequiredInformation') }));
      return;
    }

    const debateData = {
      topic,
      description,
      isPublic,
      publicJoinable,
      parameters: {
        numberOfTeams: 2,
        topic
      },
      teams: [
        {
          name: teamName || 'The Musketeers',
          viewpoint: teamViewpoint || customPrompt,
          isAI: false,
          maxMembers: teamSize,
          publicSpots: publicSpots
        },
        {
          name: opponentTeamName || 'The Guardians',
          viewpoint: customCounterPrompt,
          isAI: false,
          maxMembers: opponentTeamSize,
          publicSpots: opponentPublicSpots
        }
      ],
      views: [
        {
          prompt: customPrompt || generatedViews.prompt,
          counterPrompt: customCounterPrompt || generatedViews.counterPrompt
        }
      ]
    };

    try {
      setLoading(true);
      const newDebate = await startNewDebate(
        topic,
        description,
        {
          views: [{
            prompt: customPrompt,
            counterPrompt: customCounterPrompt
          }],
          teams: debateData.teams,
          parameters: debateData.parameters,
          isPublic,
          publicJoinable,
          // Pass optional invite password settings to server
          invitePasswordEnabled,
          invitePassword: invitePasswordEnabled ? invitePassword : undefined
        }
      );

      logger.info('Debate created:', newDebate);
      setCreatedDebate(newDebate);
      
      // Generate invite code for the debate (for opponent team)
      const inviteResponse = await axios.post(makeApiPath(`/debates/${newDebate.id}/invite`));
      const base = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';
      const sharePreview = `${base}/api/debates/share/invite/${inviteResponse.data.inviteCode}`;
      setInviteLink(sharePreview.replace(/\/\/+/g, '/').replace('http:/', 'http://').replace('https:/', 'https://'));
      logger.info('Generated opponent invite link:', inviteResponse.data.inviteLink);
      
      // Generate team-specific invite for creator's team (if team size > 1)
      if (teamSize > 1) {
        try {
          logger.info('Generating team invite link for team:', newDebate.teams[0]);
          // Get team id - handle both id formats
          const team = newDebate.teams[0];
          // Using type assertion to handle potential MongoDB _id field
          const teamId = team.id || (team as any)._id;
          
          if (!teamId) {
            logger.error('Team ID not found in the new debate response:', team);
            toast.error('Could not generate team invite link: Team ID not found');
          } else {
            const teamInviteResponse = await axios.post(
              makeApiPath(`/debates/${newDebate.id}/teams/${teamId}/invite`)
            );
            logger.info('Team invite response:', teamInviteResponse.data);
            const base2 = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:5000';
            const teamSharePreview = `${base2}/api/debates/share/team-invite/${teamInviteResponse.data.inviteCode}`;
            setTeamInviteLink(teamSharePreview.replace(/\/\/+/g, '/').replace('http:/', 'http://').replace('https:/', 'https://'));
          }
        } catch (err) {
          logger.error('Failed to generate team invite link:', err);
          toast.error('Could not generate team invite link');
        }
      }
      
      setInviteDialogOpen(true);
      toast.success(t('debateCreatedSuccessfully'));
    } catch (error) {
      logger.error('Error creating debate:', error);
      toast.error('Failed to create debate');
    } finally {
      setLoading(false);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0) {
      handleGenerateViews();
    } else if (activeStep === 1) {
      handleCustomizeViews();
    } else if (activeStep === 2) {
      handleCustomizeTeam();
    } else if (activeStep === 3) {
      handleCreateDebate();
    }
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle invite link copy
  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success(t('inviteLinkCopied'));
  };

  // Handle invite dialog close and navigate to debate
  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    if (createdDebate?.id) {
      navigate(`/debate/${createdDebate.id}`);
    }
  };

  // Handle sharing invite link (mobile)
  const handleShareInviteLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join my debate: ${topic}`,
          text: 'I\'ve invited you to join a debate on Gauntlet Debates!',
          url: inviteLink
        });
        toast.success('Shared successfully!');
      } catch (err) {
        logger.error('Error sharing:', err);
      }
    } else {
      handleCopyInviteLink();
    }
  };
  
  // Function to swap the viewpoints
  const handleSwapViews = () => {
    const tempPrompt = customPrompt;
    setCustomPrompt(customCounterPrompt);
    setCustomCounterPrompt(tempPrompt);
  };

  // Enforce constraint on public spots
  useEffect(() => {
    // Ensure public spots can't exceed max team size - 1 for creator's team
    if (publicSpots > teamSize - 1 && teamSize > 1) {
      setPublicSpots(teamSize - 1);
    }
    
    // Ensure public spots can't exceed max team size for opponent team
    if (opponentPublicSpots > opponentTeamSize) {
      setOpponentPublicSpots(opponentTeamSize);
    }
    
    // If public spots are set but debate is not publicly joinable, reset them
    if (!publicJoinable && (publicSpots > 0 || opponentPublicSpots > 0)) {
      setPublicSpots(0);
      setOpponentPublicSpots(0);
    }
    
    // If debate is not public, it can't be publicly joinable
    if (!isPublic && publicJoinable) {
      setPublicJoinable(false);
    }
  }, [teamSize, opponentTeamSize, publicSpots, opponentPublicSpots, publicJoinable, isPublic]);

  // Render team configuration step
  const renderCustomizeStep = () => {
  return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('configureTeamsAndDebateSettings')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {/* Public/Private Debate Selection */}
        <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('debateVisibility')}
          </Typography>
          
          <FormControl component="fieldset">
            <RadioGroup
              value={isPublic ? "public" : "private"}
              onChange={(e) => setIsPublic(e.target.value === "public")}
            >
              <FormControlLabel 
                value="public" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="subtitle1">{t('publicDebate')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('publicDebateDescription')}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel 
                value="private" 
                control={<Radio />} 
                label={
                  <Box>
                    <Typography variant="subtitle1">{t('privateDebate')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('privateDebateDescription')}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
          
          {isPublic && (
            <Box sx={{ mt: 2, ml: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={publicJoinable}
                    onChange={(e) => setPublicJoinable(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('makeDebatePubliclyJoinable')}
                  </Typography>
                }
              />
            </Box>
          )}

          {/* Optional password protection for opponent invite (applies to public or private) */}
          <Box sx={{ mt: 2, ml: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={invitePasswordEnabled}
                  onChange={(e) => setInvitePasswordEnabled(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  {t('protectOpponentInviteWithPassword', 'Require a password to accept the invite link')}
                </Typography>
              }
            />
            {invitePasswordEnabled && (
              <TextField
                type="text"
                size="small"
                label={t('invitePasswordLabel', 'Invite Password')}
                value={invitePassword}
                onChange={(e) => setInvitePassword(e.target.value)}
                inputProps={{ maxLength: 64 }}
                sx={{ mt: 1, maxWidth: 360 }}
                helperText={t('invitePasswordHelp', 'Both the opponent and team invite links require this password. Share it privately with invited participants.')}
              />
            )}
          </Box>
        </Paper>
        
        <Grid container spacing={3}>
          {/* Your Team Configuration */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('yourTeamName')}
              </Typography>
              
              <TextField
                fullWidth
                label={t('yourTeamName')}
                value={teamName}
                onChange={(e) => {
                  setTeamName(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.teamName) {
                    setValidationErrors(prev => ({ ...prev, teamName: '' }));
                  }
                }}
                margin="normal"
                variant="outlined"
                error={!!validationErrors.teamName}
                helperText={validationErrors.teamName}
              />
              
              <FormControl fullWidth margin="normal">
                <FormLabel>{t('teamSize')} {t('includingYouText')}</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    value={teamSize}
                    onChange={(e) => setTeamSize(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: 100 }}
                  />
                  <Tooltip title={t('teamSizeTooltip')}>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </FormControl>
              
              {publicJoinable && (
                <FormControl fullWidth margin="normal">
                  <FormLabel>{t('publicSpots')}</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      type="number"
                      value={publicSpots}
                      onChange={(e) => setPublicSpots(Math.max(0, Math.min(teamSize - 1, parseInt(e.target.value) || 0)))}
                      inputProps={{ min: 0, max: teamSize > 1 ? teamSize - 1 : 0 }}
                      sx={{ width: 100 }}
                      disabled={teamSize <= 1}
                    />
                    <Tooltip title={teamSize <= 1 ? t('increaseTeamSizeTooltip') : t('publicSpotsTooltip')}>
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </FormControl>
              )}
            </Card>
          </Grid>
          
          {/* Opponent Team Configuration */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('opponentTeamName')}
              </Typography>
              
              <TextField
                fullWidth
                label={t('opponentTeamName')}
                value={opponentTeamName}
                onChange={(e) => {
                  setOpponentTeamName(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.opponentTeamName) {
                    setValidationErrors(prev => ({ ...prev, opponentTeamName: '' }));
                  }
                }}
                margin="normal"
                variant="outlined"
                error={!!validationErrors.opponentTeamName}
                helperText={validationErrors.opponentTeamName}
              />
              
              <FormControl fullWidth margin="normal">
                <FormLabel>{t('teamSize')}</FormLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    type="number"
                    value={opponentTeamSize}
                    onChange={(e) => setOpponentTeamSize(Math.max(1, parseInt(e.target.value) || 1))}
                    inputProps={{ min: 1, max: 10 }}
                    sx={{ width: 100 }}
                  />
                  <Tooltip title={t('opponentTeamSizeTooltip')}>
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </FormControl>
              
              {publicJoinable && (
                <FormControl fullWidth margin="normal">
                  <FormLabel>{t('publicSpots')}</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextField
                      type="number"
                      value={opponentPublicSpots}
                      onChange={(e) => setOpponentPublicSpots(Math.max(0, Math.min(opponentTeamSize, parseInt(e.target.value) || 0)))}
                      inputProps={{ min: 0, max: opponentTeamSize }}
                      sx={{ width: 100 }}
                    />
                    <Tooltip title={t('opponentPublicSpotsTooltip')}>
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </FormControl>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  // Render overview step (formerly invite opponent)
  const renderOverviewStep = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('debateOverview')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('reviewAllDetailsDescription')}
          </Typography>
        </Alert>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('debateInformation')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('topicLabel')}</strong> {topic}
            </Typography>
            {description && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{t('descriptionLabel')}</strong> {description}
              </Typography>
            )}
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('visibilityLabel')}</strong> {isPublic ? t('publicDebateText') : t('privateDebateText')}
            </Typography>
            {isPublic && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{t('publiclyJoinableLabel')}</strong> {publicJoinable ? t('yesText') : t('noText')}
              </Typography>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              {t('yourTeamLabel')} {teamName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('yourViewpointLabel')}</strong> {customPrompt}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('teamSizeLabel')}</strong> {teamSize} {teamSize > 1 ? t('membersText') : t('memberText')} {t('includingYouText')}
            </Typography>
            {publicJoinable && teamSize > 1 && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{t('publicSpotsLabel')}</strong> {publicSpots}
              </Typography>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              {t('opponentTeamLabel')} {opponentTeamName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('opponentViewpointLabel')}</strong> {customCounterPrompt}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{t('teamSizeLabel')}</strong> {opponentTeamSize} {opponentTeamSize > 1 ? t('membersText') : t('memberText')}
            </Typography>
            {publicJoinable && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{t('publicSpotsLabel')}</strong> {opponentPublicSpots}
              </Typography>
            )}
          </CardContent>
        </Card>

        {!isAuthenticated && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('needToBeLoggedInToCreate')}
          </Alert>
        )}
      </Box>
    );
  };

  // Function to render the appropriate step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('debateFormHeaderText')}
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{t('productiveDebateTipsText')}</strong> {t('debateTopicGuidanceText')}
              </Typography>
            </Alert>
            <TextField
              fullWidth
              label={t('debateTopicLabel')}
              variant="outlined"
              value={topic}
              onChange={(e) => {
                setTopic(e.target.value);
                // Clear validation error when user starts typing
                if (validationErrors.topic) {
                  setValidationErrors(prev => ({ ...prev, topic: '' }));
                }
              }}
              placeholder={t('placeholderExample')}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: TOPIC_MAX_LENGTH }}
              error={!!validationErrors.topic}
              helperText={
                validationErrors.topic ? (
                  <span style={{ color: 'error.main' }}>{validationErrors.topic}</span>
                ) : (
                  <>
                    {t('focusedTopicHelperText')}
                    <span style={{ float: 'right', color: topic.length > TOPIC_MAX_LENGTH * 0.8 ? 'warning.main' : 'text.secondary' }}>
                      {topic.length}/{TOPIC_MAX_LENGTH}
                    </span>
                  </>
                )
              }
            />
            <TextField
              fullWidth
              label={t('descriptionLabel')}
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('placeholderDescription')}
              multiline
              rows={3}
              sx={{ mb: 3, '& textarea': { resize: 'vertical' } }}
              inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
              helperText={
                <>
                  {t('contextHelperText')}
                  <span style={{ float: 'right', color: description.length > DESCRIPTION_MAX_LENGTH * 0.8 ? 'warning.main' : 'text.secondary' }}>
                    {description.length}/{DESCRIPTION_MAX_LENGTH}
                  </span>
                </>
              }
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('reviewAndCustomizeDebateViews')}
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                {t('aiGeneratedViewpointsDescription')}
              </Typography>
            </Alert>
            
            <Card variant="outlined" sx={{ mb: 1 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('view1DefendPosition')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  sx={{ mb: 2, '& textarea': { resize: 'vertical' } }}
                  inputProps={{ maxLength: VIEW_MAX_LENGTH }}
                  helperText={
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography variant="caption" color={customPrompt.length > VIEW_MAX_LENGTH * 0.8 ? 'warning.main' : 'text.secondary'}>
                        {customPrompt.length}/{VIEW_MAX_LENGTH}
                      </Typography>
                    </span>
                  }
                />
              </CardContent>
            </Card>
            
            {/* Swap button between the two views */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<SwapIcon />}
                onClick={handleSwapViews}
                size="small"
              >
                {t('swapViews')}
              </Button>
            </Box>
            
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('view2OpponentPosition')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={customCounterPrompt}
                  onChange={(e) => setCustomCounterPrompt(e.target.value)}
                  sx={{ '& textarea': { resize: 'vertical' } }}
                  inputProps={{ maxLength: VIEW_MAX_LENGTH }}
                  helperText={
                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography variant="caption" color={customCounterPrompt.length > VIEW_MAX_LENGTH * 0.8 ? 'warning.main' : 'text.secondary'}>
                        {customCounterPrompt.length}/{VIEW_MAX_LENGTH}
                      </Typography>
                    </span>
                  }
                />
              </CardContent>
            </Card>
          </Box>
        );
      case 2:
        return renderCustomizeStep();
      case 3:
        return renderOverviewStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {t('startANewDebate')}
        </Typography>
                
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {validationErrors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {validationErrors.general}
          </Alert>
        )}
        
        {getStepContent(activeStep)}
        
        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={activeStep === 0 ? () => navigate('/') : handleBack}
            disabled={loading}
          >
            {activeStep === 0 ? t('cancel') : t('back')}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={loading || contextLoading}
          >
            {loading || contextLoading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              t('createDebate')
            ) : (
              t('next')
            )}
          </Button>
        </Box>
      </Paper>

      {/* Invite Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('debateCreatedSuccessfullyText')}</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('debateCreatedMessageText')}
          </Alert>
          
          {teamSize > 1 && (
            <>
              <Typography variant="h6" gutterBottom>
                {t('inviteMembersText')} ({teamName})
              </Typography>
              <Typography variant="body2" gutterBottom>
                {t('shareTeamLinkText')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={teamInviteLink}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => {
                        navigator.clipboard.writeText(teamInviteLink);
                        toast.success('Your team invite link copied!');
                      }} edge="end">
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}
          
          <Typography variant="h6" gutterBottom>
            {opponentTeamSize > 1 ? t('inviteOpponentsText') : t('inviteOpponentText')} ({opponentTeamName})
          </Typography>
          <Typography variant="body2" gutterBottom>
            {opponentTeamSize > 1 ? t('shareOpponentsLinkText') : t('shareOpponentLinkText')}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={inviteLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => {
                    navigator.clipboard.writeText(inviteLink);
                    toast.success('Opponent invite link copied!');
                  }} edge="end">
                    <CopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={handleCopyInviteLink}
              fullWidth
            >
              {opponentTeamSize > 1 ? t('copyOpponentsLinkText') : t('copyOpponentLinkText')}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={handleShareInviteLink}
              fullWidth
            >
              {t('shareText')}
            </Button>
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            {t('linksWorkUntilText')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            startIcon={<MessageIcon />}
            onClick={handleCloseInviteDialog}
          >
            {t('goToDebateRoomText')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DebateFormPage; 